import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, CreditCard, Shield, Clock } from "lucide-react";
import type { Plan } from "@/lib/finance-plans";

interface PayerDetails {
  fullName: string;
  email: string;
  phone: string;
}

interface PaymentConfirmationProps {
  amountAED: number;
  financingPlan: Plan;
  payerDetails: PayerDetails;
  onBack: () => void;
  onConfirm: () => void;
}

export default function PaymentConfirmation({ amountAED, financingPlan, payerDetails, onBack, onConfirm }: PaymentConfirmationProps) {
  const processingFee = Math.round(amountAED * 0.025); // 2.5% processing fee
  const totalAmount = amountAED + processingFee;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h3 className="text-xl font-semibold">Confirm Your Payment</h3>
          <p className="text-sm text-muted-foreground">
            Review your details before proceeding
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Payment Summary */}
        <Card className="p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Summary
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Course Fee:</span>
              <span>AED {amountAED.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Processing Fee:</span>
              <span>AED {processingFee.toLocaleString()}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold text-base">
              <span>Total Amount:</span>
              <span>AED {totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </Card>

        {/* Financing Plan Details */}
        <Card className="p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Selected Financing Plan
          </h4>
          <div className="space-y-3">
            <div>
              <div className="font-medium">{financingPlan.label}</div>
              {financingPlan.notes && financingPlan.notes.length > 0 && (
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                  {financingPlan.notes.map((note, idx) => (
                    <li key={idx}>• {note}</li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {financingPlan.breakdown.bnplAED && (
                <div className="bg-blue-50 p-3 rounded">
                  <div className="font-medium text-blue-900">BNPL</div>
                  <div className="text-blue-700">AED {financingPlan.breakdown.bnplAED.toLocaleString()}</div>
                </div>
              )}
              
              {financingPlan.breakdown.bankFinanceAED && (
                <div className="bg-green-50 p-3 rounded">
                  <div className="font-medium text-green-900">Bank Finance</div>
                  <div className="text-green-700">AED {financingPlan.breakdown.bankFinanceAED.toLocaleString()}</div>
                </div>
              )}
              
              {financingPlan.breakdown.upfrontAED && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium text-gray-900">Upfront</div>
                  <div className="text-gray-700">AED {financingPlan.breakdown.upfrontAED.toLocaleString()}</div>
                </div>
              )}
            </div>

            {financingPlan.tenureMonths && (
              <div className="text-sm text-muted-foreground">
                Tenure: {financingPlan.tenureMonths} months
                {financingPlan.aprPercent && ` • APR: ${financingPlan.aprPercent}%`}
              </div>
            )}
          </div>
        </Card>

        {/* Payer Details */}
        <Card className="p-4">
          <h4 className="font-medium mb-4">Guardian Information</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium">{payerDetails.fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span>{payerDetails.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone:</span>
              <span>{payerDetails.phone}</span>
            </div>
          </div>
        </Card>

        {/* Important Notice */}
        <div className="bg-muted p-4 rounded-md">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium mb-1">Important Notice</p>
              <p className="text-xs text-muted-foreground">
                This enrollment confirmation is subject to school approval and available capacity. 
                You will receive a confirmation email with next steps within 24 hours. 
                Please ensure you have the school invoice ready for upload.
              </p>
            </div>
          </div>
        </div>

        <Button onClick={onConfirm} className="w-full" size="lg">
          Confirm Payment & Enroll
        </Button>
      </div>
    </Card>
  );
}